import { accountKeys, entryKeys, transferKeys } from '@taler/api-client'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, reactive, ref } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import type { TransferView } from '../internal/transfer-view.ts'

export interface UseInbox {
  readonly items: ComputedRef<readonly TransferView[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  /**
   * Transfers this session just accepted, kept in memory only: the contract has no
   * endpoint listing a ledger's past transfers, so this is the one place a reversal can
   * still name a `transferId` without the user having written it down.
   */
  readonly justAccepted: ComputedRef<readonly TransferView[]>
  isBusy(transferId: string): boolean
  accept(transfer: TransferView, targetAccountId: string): Promise<void>
  reject(transfer: TransferView): Promise<void>
  reverse(transfer: TransferView): Promise<void>
  retry(): void
}

export function useInbox(): UseInbox {
  const api = useApi()
  const queryClient = useQueryClient()
  const busy = reactive(new Set<string>())
  const justAccepted = ref<TransferView[]>([])

  const query = useQuery({
    queryKey: transferKeys.inbox(),
    queryFn: ({ signal }) => api.transfers.inbox(signal),
    retry: false,
  })

  async function afterSettlement(transfer: TransferView): Promise<void> {
    const keys = [
      transferKeys.inbox(),
      ...entryKeys.invalidatedByWrite(transfer.sourceLedgerId),
      ...entryKeys.invalidatedByWrite(transfer.targetLedgerId),
      ...accountKeys.invalidatedByWrite(transfer.targetLedgerId),
    ]
    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
  }

  return {
    items: computed(() => query.data.value ?? []),
    isLoading: computed(() => query.isPending.value),
    error: computed(() => query.error.value),
    retrying: retrying(query),
    justAccepted: computed(() => justAccepted.value),
    isBusy: (transferId) => busy.has(transferId),
    retry: () => {
      void query.refetch()
    },
    async accept(transfer, targetAccountId) {
      busy.add(transfer.id)
      try {
        const posted = await api.transfers.accept(transfer.targetLedgerId, transfer.id, targetAccountId)
        justAccepted.value = [posted, ...justAccepted.value]
        await afterSettlement(transfer)
      } finally {
        busy.delete(transfer.id)
      }
    },
    async reject(transfer) {
      busy.add(transfer.id)
      try {
        await api.transfers.reject(transfer.targetLedgerId, transfer.id)
        await afterSettlement(transfer)
      } finally {
        busy.delete(transfer.id)
      }
    },
    async reverse(transfer) {
      busy.add(transfer.id)
      try {
        await api.transfers.reverse(transfer.targetLedgerId, transfer.id)
        justAccepted.value = justAccepted.value.filter((candidate) => candidate.id !== transfer.id)
        await afterSettlement(transfer)
      } finally {
        busy.delete(transfer.id)
      }
    },
  }
}
